import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Section = ({ title, text, children, testid }) => (
  <section className="surface space-y-4 p-5" data-testid={testid}>
    <div><h2 className="text-2xl">{title}</h2>{text && <p className="mt-1 text-sm text-slate-500">{text}</p>}</div>
    {children}
  </section>
);

const Field = ({ label, hint, children }) => (
  <label className="block space-y-1 text-sm"><span className="font-medium text-slate-700">{label}</span>{children}{hint && <span className="block text-xs text-slate-400">{hint}</span>}</label>
);

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl bg-[#F5F1EB] p-3"><p className="text-xs text-slate-500">{label}</p><p className="text-2xl font-semibold" data-testid={testid}>{value}</p></div>
);

export const AiUsage = ({ d }) => {
  const u = d.usage;
  return (
    <Section title="AI usage" text="Counted per UTC day. Member-content screening isn't stopped by the cap, so posting never breaks; everything else pauses until tomorrow." testid="ai-usage-section">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Capped calls today" value={`${u.capped || 0} / ${u.cap}`} testid="usage-capped" />
        <Stat label="All calls today" value={u.total || 0} testid="usage-total" />
        <Stat label="Claims in fact database" value={d.stats.claims} testid="stat-claims" />
        <Stat label="Checks saved by reuse" value={d.stats.reused} testid="stat-reused" />
        <Stat label="Waiting in queue" value={d.stats.queued} testid="stat-queued" />
      </div>
      <div className="overflow-x-auto"><table className="w-full text-left text-sm" data-testid="usage-days-table">
        <thead className="text-xs text-slate-500"><tr><th className="py-1 pr-4">Day</th><th className="pr-4">Total</th><th className="pr-4">Capped</th><th>By purpose</th></tr></thead>
        <tbody>{d.days.map((x) => (
          <tr key={x.day} className="border-t border-border align-top"><td className="py-1.5 pr-4">{x.day}</td><td className="pr-4">{x.total}</td><td className="pr-4">{x.capped}</td>
            <td className="text-xs text-slate-600">{Object.entries(x.by_purpose || {}).map(([k, v]) => `${k.replace("_", " ")} ${v}`).join(" · ")}</td></tr>
        ))}</tbody>
      </table></div>
    </Section>
  );
};

export const ModelSettings = ({ ai, onSave }) => {
  const [f, setF] = useState(ai);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Section title="Models & limits" text="Changes apply immediately to every AI call. Keys stay on the server." testid="model-settings-section">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Provider"><Select value={f.provider} onValueChange={(v) => setF({ ...f, provider: v })}>
          <SelectTrigger data-testid="ai-provider-select"><SelectValue /></SelectTrigger>
          <SelectContent>{["anthropic", "openai", "gemini"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Cheap model" hint="Screening, claim detection, news triage"><Input value={f.triage_model} onChange={set("triage_model")} data-testid="ai-triage-model" /></Field>
        <Field label="Standard model" hint="Ordinary claims and news checks"><Input value={f.default_model} onChange={set("default_model")} data-testid="ai-default-model" /></Field>
        <Field label="Strongest model" hint="High-risk claims only"><Input value={f.deep_model} onChange={set("deep_model")} data-testid="ai-deep-model" /></Field>
        <Field label="Daily cap (AI calls)"><Input type="number" min={0} value={f.daily_cap} onChange={set("daily_cap")} data-testid="ai-daily-cap" /></Field>
        <Field label="Send to human review below confidence" hint="0 to 1 (e.g. 0.75)"><Input type="number" step="0.05" min={0} max={1} value={f.review_below} onChange={set("review_below")} data-testid="ai-review-below" /></Field>
      </div>
      <Button className="rounded-full" onClick={() => onSave({ ...f, daily_cap: Number(f.daily_cap), review_below: Number(f.review_below) })} data-testid="ai-settings-save">Save models & limits</Button>
    </Section>
  );
};

const Checks = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-3">{options.map((o) => (
    <label key={o} className="flex items-center gap-1.5 text-sm capitalize">
      <Checkbox checked={value.includes(o)} onCheckedChange={(c) => onChange(c ? [...value, o] : value.filter((x) => x !== o))} data-testid={`${testid}-${o}`} />{o}
    </label>
  ))}</div>
);

const NUMS = [["min_chars", "Ignore content shorter than (characters)"], ["reach_threshold", "Check when reactions + comments + shares reach"], ["reuse_days", "Reuse a checked claim for (days)"],
  ["insufficient_retry_days", "Retry 'not enough evidence' after (days)"], ["similarity", "Match an existing claim at similarity (0–1)"], ["evidence_limit", "Evidence records per check"], ["per_tick", "Queued checks per 2-minute worker run"]];

export const FactCheckRules = ({ s, topics, onSave }) => {
  const [f, setF] = useState(s);
  return (
    <Section title="Fact-check rules" text="Everyday posts get a full check only when the claim is on a risky topic or the post spreads past the reach threshold. Everything else waits in the queue with a 'Not yet checked' tag." testid="factcheck-rules-section">
      <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="fc-enabled-switch" />Fact-check member content</label>
      <Field label="Content types"><Checks options={["post", "short"]} value={f.kinds} onChange={(v) => setF({ ...f, kinds: v })} testid="fc-kind" /></Field>
      <Field label="Risky topics (checked immediately)"><Checks options={topics} value={f.risk_topics} onChange={(v) => setF({ ...f, risk_topics: v })} testid="fc-topic" /></Field>
      <Field label="Use the strongest model for risk levels"><Checks options={["low", "medium", "high"]} value={f.deep_for_risk} onChange={(v) => setF({ ...f, deep_for_risk: v })} testid="fc-deep" /></Field>
      <div className="grid gap-4 sm:grid-cols-2">{NUMS.map(([k, label]) => (
        <Field key={k} label={label}><Input type="number" step={k === "similarity" ? "0.05" : "1"} min={0} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`fc-${k}`} /></Field>
      ))}</div>
      <Button className="rounded-full" onClick={() => onSave({ ...f, ...Object.fromEntries(NUMS.map(([k]) => [k, Number(f[k])])), evidence_sources: undefined })} data-testid="fc-rules-save">Save rules</Button>
    </Section>
  );
};
